import { formatCop } from "@/lib/credits";

export const DEFAULT_CREDIT_ALERT_THRESHOLDS_COP = [20000, 10000, 5000];
export const DEFAULT_VOICE_ALERT_THRESHOLDS_COP = [50000, 20000, 10000];
export const MAX_CREDIT_ALERT_THRESHOLDS = 10;
export const MAX_CREDIT_ALERT_THRESHOLD_COP = 10_000_000;

export interface CreditBalanceAlertState {
  organizationId: string;
  balanceCop: number;
  thresholdCop: number;
  armLedgerId: number;
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function parseCreditBalanceAlert(raw: unknown, orgId: string | null): CreditBalanceAlertState | null {
  if (!orgId || !raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (row.organization_id !== orgId || row.enrolled !== true || row.exhausted === true) return null;
  const balanceCop = num(row.balance_cop);
  const thresholdCop = num(row.threshold_cop);
  const armLedgerId = num(row.arm_ledger_id);
  if (balanceCop === null || thresholdCop === null || armLedgerId === null) return null;
  return { organizationId: orgId, balanceCop, thresholdCop, armLedgerId };
}

export function creditBalanceAlertDismissKey(alert: CreditBalanceAlertState): string {
  return `credit-balance-alert:${alert.organizationId}:${alert.armLedgerId}:${alert.thresholdCop}`;
}

export function creditBalanceAlertCopy(alert: CreditBalanceAlertState): { title: string; detail: string } {
  return {
    title: `Tu saldo de créditos bajó de ${formatCop(alert.thresholdCop)}.`,
    detail: `Te quedan ${formatCop(alert.balanceCop)}. Cuando no alcance para una conversación, la IA dejará de responder hasta que recargues.`,
  };
}

export type ParsedAlertThresholds = { ok: true; value: number[] } | { ok: false; error: string };

export function parseAlertThresholdsInput(raw: string): ParsedAlertThresholds {
  const parts = raw.split(/[,;\n]/).map((p) => p.trim()).filter((p) => p !== "");
  const values: number[] = [];
  for (const part of parts) {
    const cleaned = part.replace(/[$.\s]/g, "");
    if (!/^\d+$/.test(cleaned)) return { ok: false, error: `"${part}" no es un valor en pesos.` };
    const value = Number(cleaned);
    if (!Number.isSafeInteger(value) || value <= 0 || value > MAX_CREDIT_ALERT_THRESHOLD_COP) {
      return { ok: false, error: "Cada umbral debe ser un valor entero en pesos, mayor que 0." };
    }
    values.push(value);
  }
  const unique = [...new Set(values)].sort((a, b) => b - a);
  if (unique.length > MAX_CREDIT_ALERT_THRESHOLDS) {
    return { ok: false, error: `Máximo ${MAX_CREDIT_ALERT_THRESHOLDS} umbrales.` };
  }
  return { ok: true, value: unique };
}

export function formatAlertThresholds(values: number[]): string {
  return values.map((v) => Math.round(v).toLocaleString("es-CO")).join(", ");
}
